import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ImageUpload from './ImageUpload';
import { useAuth } from '../../context/AuthContext';
import API_CONFIG from '../../apiConfig';
import { useCreditEstimation } from '../../hooks/useCreditEstimation';
import { useModeRestrictions } from '../../hooks/useModeRestrictions';
import CreditCostPreview from '../ui/CreditCostPreview';
import InsufficientCreditsModal from '../ui/InsufficientCreditsModal';
import { examplePresets } from './examplePresets';
import CustomSelect from '../ui/CustomSelect';

interface ImageEditingSettings {
  prompt: string;
  mode: string;
  numImages: number;
}

interface LocationState {
  inputImages: string[];
  generatedImages?: string[];
}

const ImageEditing: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState;

  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [generatedImages, setGeneratedImages] = useState<string[]>(state?.generatedImages || []);
  const [generationTime, setGenerationTime] = useState<number>(0);
  const [timerInterval, setTimerInterval] = useState<NodeJS.Timeout | null>(null);
  const { currentUser, refreshUserCredits, userCredits } = useAuth();

  // Settings state
  const [settings, setSettings] = useState<ImageEditingSettings>({
    prompt: '',
    mode: 'performance',
    numImages: 1
  });

  const [errorMessage, setErrorMessage] = useState('');
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
  const [isExample, setIsExample] = useState(false);

  // Credit estimation hook
  const [creditEstimation, estimateCreditCost] = useCreditEstimation('image-editing');

  // Mode restrictions hook - pass true to show "Balanced (Nano Banana)" label
  const modeRestrictions = useModeRestrictions(true);

  // Effect to handle recreation state
  useEffect(() => {
    const loadInputImages = async () => {
      if (state?.inputImages && state.inputImages.length >= 1) {
        try {
          setImageUrls(state.inputImages.slice(0, 5));
          if (state.generatedImages) {
            setGeneratedImages(state.generatedImages);
          }
        } catch (error) {
          console.error('Failed to load input images:', error);
        }
      }
    };

    loadInputImages();
  }, [state]);

  // Clear error messages when user uploads new images
  useEffect(() => {
    if (imageUrls.length > 0) {
      setErrorMessage('');
    }
  }, [imageUrls]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [timerInterval]);

  // Handle mode restrictions
  useEffect(() => {
    if (modeRestrictions.hasInitialLoad) {
      setSettings((prevSettings) => {
        if (!modeRestrictions.canUseMode(prevSettings.mode)) {
          const availableMode = modeRestrictions.availableModes.find(m => !m.disabled);
          if (availableMode) {
            return { ...prevSettings, mode: availableMode.value };
          }
        }
        return prevSettings;
      });
    }
  }, [modeRestrictions.hasInitialLoad, modeRestrictions.availableModes, modeRestrictions.canUseMode]);

  // Handle Performance mode restriction when more than 3 images are selected
  useEffect(() => {
    if (imageUrls.length > 3) {
      setSettings((prevSettings) => {
        if (prevSettings.mode === 'performance') {
          // Performance mode can only handle max 3 images, switch to an available mode
          const availableMode = modeRestrictions.availableModes.find(
            m => m.value !== 'performance' && !m.disabled
          );
          if (availableMode) {
            return { ...prevSettings, mode: availableMode.value };
          }
        }
        return prevSettings;
      });
    }
  }, [imageUrls.length, modeRestrictions.availableModes]);

  // Credit estimation effect
  useEffect(() => {
    if (!currentUser?.uid || imageUrls.length === 0) {
      return;
    }

    const estimateCost = async () => {
      const estimationPayload = {
        inputImageUrl1: imageUrls[0],
        inputImageUrl2: imageUrls[1] || null,
        inputImageUrl3: imageUrls[2] || null,
        inputImageUrl4: imageUrls[3] || null,
        inputImageUrl5: imageUrls[4] || null,
        prompt: settings.prompt,
        numImages: settings.numImages,
        mode: settings.mode.toLowerCase(),
        userId: currentUser?.uid || ''
      };

      estimateCreditCost(estimationPayload);
    };

    estimateCost();
  }, [
    currentUser?.uid,
    imageUrls,
    settings.prompt,
    settings.numImages,
    settings.mode,
    estimateCreditCost
  ]);

  const startTimer = () => {
    setGenerationTime(0);
    const interval = setInterval(() => {
      setGenerationTime(prev => prev + 0.1);
    }, 100);
    setTimerInterval(interval);
  };

  const stopTimer = () => {
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }
  };

  const handleExampleSelect = (imageUrl: string, exampleId: number, isSecondImage?: boolean) => {
    setIsExample(true);
    
    const example = examplePresets.find(p => p.id === exampleId);
    if (!example) return;

    // Load input images
    const inputImages = isSecondImage 
      ? [imageUrls[0] || example.preview, imageUrl]
      : [imageUrl, example.preview2].filter((url): url is string => Boolean(url));
    
    setImageUrls(inputImages);

    // Load prompt
    setSettings({
      ...settings,
      prompt: example.prompt
    });

    // Load results
    setGeneratedImages(example.results);
    setErrorMessage('');
  };

  const handleGenerate = async () => {
    if (imageUrls.length === 0) {
      alert('Please upload at least one image');
      return;
    }

    if (!settings.prompt.trim()) {
      alert('Please enter a prompt describing the desired editing');
      return;
    }

    if (!creditEstimation.canAfford) {
      setShowInsufficientCreditsModal(true);
      return;
    }

    setIsProcessing(true);
    setGeneratedImages([]);
    setIsExample(false);
    setErrorMessage('');
    startTimer();

    try {
      const payload = {
        inputImageUrl1: imageUrls[0],
        inputImageUrl2: imageUrls[1] || null,
        inputImageUrl3: imageUrls[2] || null,
        inputImageUrl4: imageUrls[3] || null,
        inputImageUrl5: imageUrls[4] || null,
        imageSize: 'square',
        prompt: settings.prompt,
        numImages: settings.numImages,
        outputFormat: 'webp',
        enableSafetyChecker: true,
        userId: currentUser?.uid || 'user_id',
        mode: settings.mode.toLowerCase()
      };

      const response = await fetch(API_CONFIG.IMAGE_EDITING_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        setErrorMessage(errorData.message || 'Something went wrong. Please try again.');
        return;
      }

      const data = await response.json();

      if (data.success && Array.isArray(data.imageUrls) && data.imageUrls.length > 0) {
        setGeneratedImages(data.imageUrls);
        await refreshUserCredits();
        setErrorMessage('');
      } else {
        setErrorMessage(data.message || 'Failed to edit image. Please try again.');
      }
    } catch (error) {
      console.error('Failed to edit image:', error);
      setErrorMessage('Failed to edit image. Please try again.');
    } finally {
      stopTimer();
      setIsProcessing(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-3">
      {/* Grid Layout - 4 columns on laptop, Results takes 2 columns */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
        {/* Column 1: All Image Uploads */}
        <div className="lg:col-span-1 space-y-4">
          {/* First Image Upload - Main */}
          <ImageUpload
            imageUrls={imageUrls.length > 0 ? [imageUrls[0]] : []}
            setImageUrls={(urls: React.SetStateAction<string[]>) => {
              const urlsArray = typeof urls === 'function' ? urls([]) : urls;
              const newUrls: string[] = [...imageUrls];
              if (urlsArray.length > 0) {
                newUrls[0] = urlsArray[0];
              } else {
                newUrls.splice(0, 1);
              }
              setImageUrls(newUrls);
            }}
            title="Input Image 1"
            isExample={isExample}
          />

          {/* Optional Images Grid */}
          <div className="grid grid-cols-2 gap-3">
            {/* Second Image Upload */}
            <ImageUpload
              imageUrls={imageUrls.length > 1 ? [imageUrls[1]] : []}
              setImageUrls={(urls: React.SetStateAction<string[]>) => {
                const urlsArray = typeof urls === 'function' ? urls([]) : urls;
                const newUrls: string[] = [...imageUrls];
                if (urlsArray.length > 0) {
                  newUrls[1] = urlsArray[0];
                } else if (newUrls.length > 1) {
                  newUrls.splice(1, 1);
                }
                setImageUrls(newUrls);
              }}
              title="Image 2 (Optional)"
              isExample={isExample}
            />

            {/* Third Image Upload */}
            <ImageUpload
              imageUrls={imageUrls.length > 2 ? [imageUrls[2]] : []}
              setImageUrls={(urls: React.SetStateAction<string[]>) => {
                const urlsArray = typeof urls === 'function' ? urls([]) : urls;
                const newUrls: string[] = [...imageUrls];
                if (urlsArray.length > 0) {
                  newUrls[2] = urlsArray[0];
                } else if (newUrls.length > 2) {
                  newUrls.splice(2, 1);
                }
                setImageUrls(newUrls);
              }}
              title="Image 3 (Optional)"
              isExample={isExample}
            />

            {/* Fourth Image Upload */}
            <ImageUpload
              imageUrls={imageUrls.length > 3 ? [imageUrls[3]] : []}
              setImageUrls={(urls: React.SetStateAction<string[]>) => {
                const urlsArray = typeof urls === 'function' ? urls([]) : urls;
                const newUrls: string[] = [...imageUrls];
                if (urlsArray.length > 0) {
                  newUrls[3] = urlsArray[0];
                } else if (newUrls.length > 3) {
                  newUrls.splice(3, 1);
                }
                setImageUrls(newUrls);
              }}
              title="Image 4 (Optional)"
              isExample={isExample}
            />

            {/* Fifth Image Upload */}
            <ImageUpload
              imageUrls={imageUrls.length > 4 ? [imageUrls[4]] : []}
              setImageUrls={(urls: React.SetStateAction<string[]>) => {
                const urlsArray = typeof urls === 'function' ? urls([]) : urls;
                const newUrls: string[] = [...imageUrls];
                if (urlsArray.length > 0) {
                  newUrls[4] = urlsArray[0];
                } else if (newUrls.length > 4) {
                  newUrls.splice(4, 1);
                }
                setImageUrls(newUrls);
              }}
              title="Image 5 (Optional)"
              isExample={isExample}
            />
          </div>
        </div>

        {/* Column 2: Settings Panel */}
        <div className="lg:col-span-1">
          <div className="bg-accent-sand rounded-lg p-3 space-y-3">
            <h3 className="text-sm font-semibold text-text-heading mb-2">Settings</h3>

            {/* Example Styles section */}
            <div className="bg-white rounded-lg p-3 border border-accent-taupe">
              <h4 className="text-sm font-medium text-text-heading mb-2">Example Styles</h4>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                {examplePresets.map(preset => (
                  <div
                    key={preset.id}
                    className={`relative cursor-pointer rounded-lg overflow-hidden border transition-all ${
                      isExample && imageUrls[0] === preset.preview
                        ? 'border-secondary-500 shadow-md ring-2 ring-secondary-300'
                        : 'border-accent-taupe hover:border-secondary-300'
                    }`}
                    onClick={() => handleExampleSelect(preset.preview, preset.id)}
                  >
                    <div className="aspect-ratio-box" style={{ paddingBottom: '60%', position: 'relative' }}>
                      <img
                        src={preset.preview}
                        alt={preset.name}
                        className="absolute inset-0 w-full h-full object-cover bg-accent-sand"
                      />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-xs p-1 text-center truncate">
                      {preset.name}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              {/* Mode */}
              <div>
                <label className="block text-sm font-medium text-text-heading mb-1">
                  Quality Mode
                  {!modeRestrictions.hasInitialLoad && (
                    <span className="ml-2 text-xs text-text-description">(Loading...)</span>
                  )}
                </label>
                <CustomSelect
                  value={settings.mode}
                  onChange={(value) => setSettings({ ...settings, mode: value })}
                  options={modeRestrictions.availableModes.map((modeOption) => {
                    // Disable Performance mode if more than 3 images are selected
                    const isPerformanceDisabled = modeOption.value === 'performance' && imageUrls.length > 3;
                    const isDisabled = modeOption.disabled || isPerformanceDisabled;
                    
                    let planRequirement = '';
                    if (modeOption.disabled && modeOption.requiresPlan) {
                      const minPlan = modeOption.requiresPlan[0];
                      switch (minPlan) {
                        case 'plus':
                          planRequirement = ' (Plus Plan Required)';
                          break;
                        case 'pro':
                          planRequirement = ' (Pro Plan Required)';
                          break;
                        case 'enterprise':
                          planRequirement = ' (Enterprise Plan Required)';
                          break;
                        default:
                          planRequirement = ' (Upgrade Required)';
                      }
                    }
                    
                    // Add message for Performance mode when disabled due to image count
                    let imageLimitMessage = '';
                    if (isPerformanceDisabled) {
                      imageLimitMessage = ' (Max 3 images)';
                    }

                    return {
                      value: modeOption.value,
                      label: `${modeOption.label}${planRequirement}${imageLimitMessage}`,
                      disabled: isDisabled
                    };
                  })}
                  disabled={!modeRestrictions.hasInitialLoad}
                  className="w-full"
                />
                {imageUrls.length > 3 && (
                  <p className="text-xs text-orange-600 mt-1">
                    <i className="fas fa-exclamation-triangle mr-1"></i>
                    Performance mode supports up to 3 images only. Please select Balanced or Quality mode for {imageUrls.length} images.
                  </p>
                )}
              </div>

              {/* Prompt */}
              <div>
                <label className="block text-sm font-medium text-text-heading mb-1">
                  Prompt <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={settings.prompt}
                  onChange={(e) => setSettings({ ...settings, prompt: e.target.value })}
                  placeholder="Describe the desired editing (e.g., Change the background to a beach sunset, Add flowers to the scene, Make the colors more vibrant)"
                  className="w-full border border-accent-taupe rounded-lg px-3 py-2 focus:ring-secondary-500 focus:border-secondary-500 text-sm min-h-[100px] resize-y"
                  required
                />
              </div>

              {/* Action Buttons */}
              <div className="border-t border-accent-taupe pt-3">
                {/* Clear Button */}
                <button
                  onClick={() => {
                    setImageUrls([]);
                    setSettings({
                      prompt: '',
                      mode: 'balanced',
                      numImages: 1
                    });
                    setGeneratedImages([]);
                    setIsExample(false);
                    setErrorMessage('');
                  }}
                  className="w-full py-2.5 rounded-lg font-medium text-sm transition-colors bg-red-100 hover:bg-red-200 text-red-700 border border-red-300 mb-3"
                  title="Clear all settings, images, and results"
                >
                  Clear All Settings
                </button>

                <button
                  className={`w-full py-3 rounded-lg font-medium transition-colors ${
                    imageUrls.length > 0 && settings.prompt.trim()
                      ? 'bg-secondary-500 hover:bg-secondary-600 text-white cursor-pointer'
                      : 'bg-gray-300 text-text-description cursor-not-allowed'
                  }`}
                  onClick={handleGenerate}
                  disabled={imageUrls.length === 0 || !settings.prompt.trim() || isProcessing}
                >
                  {isProcessing ? (
                    <div className="flex items-center justify-center space-x-3">
                      <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      <span>Processing...</span>
                      <div className="flex items-center space-x-1 opacity-90">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="font-mono font-semibold text-sm">
                          {generationTime.toFixed(1)}s
                        </span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <i className="fas fa-wand-magic-sparkles mr-2"></i>
                      Edit Image
                    </>
                  )}
                </button>

                {/* Credit Cost Preview - Below Generate Button */}
                <div className="mt-3">
                  <CreditCostPreview
                    creditCost={creditEstimation.creditCost}
                    isEstimating={creditEstimation.isEstimating}
                    error={creditEstimation.error}
                    canAfford={creditEstimation.canAfford}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Column 3-4: Results Section (takes 2 columns) */}
        <div className="lg:col-span-2">
          {generatedImages.length > 0 ? (
            <div>
              <h3 className="text-lg font-semibold mb-3">
                {isExample ? 'Example Results' : 'Edited Images'}
                {isExample && (
                  <span className="text-sm font-normal text-text-description ml-2">
                    (Upload your own images and click Generate to create your own)
                  </span>
                )}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {generatedImages.map((url, idx) => (
                  <div
                    key={idx}
                    className="relative flex flex-col bg-gradient-to-br from-white via-gray-50 to-gray-100 rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300 group overflow-hidden border border-accent-taupe"
                  >
                    {/* Overlay index badge */}
                    <div className="absolute top-2 left-2 z-10 bg-secondary-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full shadow">
                      #{idx + 1}
                    </div>
                    {/* Image area */}
                    <div className="flex items-center justify-center bg-accent-sand p-2">
                    <img
                      src={url}
                      alt={`${isExample ? 'Example' : 'Edited'} ${idx + 1}`}
                      className="h-[280px] w-full object-contain drop-shadow rounded-lg transition-transform duration-300 group-hover:scale-105 bg-white"
                    />
                    </div>
                    {/* Action bar */}
                    <div className="flex flex-col sm:flex-row gap-1.5 px-2 py-2 bg-white rounded-b-xl shadow-md">
                      {/* Download Button */}
                      <button
                        className="text-text-description hover:text-secondary-600 hover:bg-secondary-100 border border-accent-taupe hover:border-secondary-300 px-2 py-1.5 rounded-lg transition-colors text-xs font-medium flex-1"
                        onClick={async () => {
                          try {
                            const response = await fetch(url);
                            const blob = await response.blob();
                            const blobUrl = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = blobUrl;
                            link.download = `edited-image-${idx + 1}.jpg`;
                            link.style.display = 'none';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(blobUrl);
                          } catch (error) {
                            console.error('Download failed:', error);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `edited-image-${idx + 1}.jpg`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }
                        }}
                      >
                        Download
                      </button>
                      {/* Upscale Button */}
                      <button
                        className="text-text-description hover:text-secondary-600 hover:bg-secondary-100 border border-accent-taupe hover:border-secondary-300 px-2 py-1.5 rounded-lg transition-colors text-xs font-medium flex-1"
                        onClick={() => {
                          navigate('/canvas-editor', {
                            state: {
                              inputImages: [url],
                              tool: 'upscaler'
                            }
                          });
                        }}
                      >
                        HD
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full min-h-[400px] bg-accent-sand rounded-lg border-2 border-dashed border-accent-taupe">
              <div className="text-center p-8">
                <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-wand-magic-sparkles text-secondary-600 text-2xl"></i>
                </div>
                <h3 className="text-lg font-medium text-text-heading mb-2">No Results Yet</h3>
                <p className="text-sm text-text-description">Upload an image and enter a prompt to see edited results here</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error message display */}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-4">
          <div className="flex items-center">
            <i className="fas fa-exclamation-triangle text-red-500 mr-2"></i>
            <p className="text-sm text-red-700">{errorMessage}</p>
          </div>
        </div>
      )}

      {/* Insufficient Credits Modal */}
      <InsufficientCreditsModal
        isOpen={showInsufficientCreditsModal}
        onClose={() => setShowInsufficientCreditsModal(false)}
        requiredCredits={creditEstimation.creditCost || 0}
        availableCredits={userCredits}
        featureName="Image Editing"
      />
    </div>
  );
};

export default ImageEditing;

